import { FC } from "react";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import CloseOutlined from "@mui/icons-material/CloseOutlined";
import ArrowBackOutlined from "@mui/icons-material/ArrowBackOutlined";
import Spinner from "common/components/base/spinner/Spinner";
import { getNodeColor } from "modules/ontology/pages/create-ontology-entities-relationships/components/graph-canvas/GraphCanvasOntology";
import { OntologyNodeGraphViewNode } from "../../../../data/ontology.service";
import { AttributeRow } from "../../../../data/create-ontology-entities-relationships.data";
import {
  RelatedEntitiesConnectedNode,
  RelatedEntitiesSelectedNodeDetail
} from "./useRelatedEntitiesGraph";

interface RelatedEntitiesPanelProps {
  isInitialLoading: boolean;
  hasData: boolean;
  focusedNode: OntologyNodeGraphViewNode | null;
  connectedNodes: RelatedEntitiesConnectedNode[];
  filteredConnectedNodes: RelatedEntitiesConnectedNode[];
  search: string;
  onSearchChange: (value: string) => void;
  onClose: () => void;
  selectedNodeId: string | null;
  selectedNodeDetail: RelatedEntitiesSelectedNodeDetail | null;
  selectedNodeAttributes: AttributeRow[];
  isLoadingSelectedNodeDetail: boolean;
  onSelectNode: (nodeId: string) => void;
  onBackToConnectedList: () => void;
}

// Graph Directory panel content for the "Related Entities" view: focused node summary +
// searchable connected-nodes list. Rendered alongside RelatedEntitiesGraphOverlay, both
// driven by the same useRelatedEntitiesGraph() state.
const RelatedEntitiesPanel: FC<RelatedEntitiesPanelProps> = ({
  isInitialLoading,
  hasData,
  focusedNode,
  connectedNodes,
  filteredConnectedNodes,
  search,
  onSearchChange,
  onClose,
  selectedNodeId,
  selectedNodeDetail,
  selectedNodeAttributes,
  isLoadingSelectedNodeDetail,
  onSelectNode,
  onBackToConnectedList
}) => {
  if (selectedNodeId) {
    return (
      <div className="create-entities-related-entities-panel">
        <div className="create-entities-related-entities-panel__header">
          <button
            type="button"
            className="create-entities-graph-directory__back-btn"
            onClick={onBackToConnectedList}
          >
            <ArrowBackOutlined fontSize="small" />
            Back to nodes
          </button>
        </div>
        {isLoadingSelectedNodeDetail ? (
          <div className="create-entities-node-detail__loading">
            <Spinner size={20} />
          </div>
        ) : selectedNodeDetail ? (
          <div className="create-entities-node-detail">
            <div className="create-entities-node-detail__summary">
              <div className="create-entities-node-detail__identity">
                <span
                  className="create-entities-node-detail__swatch"
                  style={{
                    backgroundColor: getNodeColor(
                      selectedNodeDetail.conceptRow.nodeTypeRaw
                    )
                  }}
                />
                <span className="create-entities-node-detail__name">
                  {selectedNodeDetail.conceptRow.nodeName}
                </span>
                <span className="create-entities-node-detail__type-pill">
                  {selectedNodeDetail.conceptRow.nodeTypeRaw}
                </span>
              </div>
              <p className="create-entities-node-detail__focus">
                {selectedNodeDetail.conceptRow.description ||
                  "No description available."}
              </p>
            </div>

            <div className="create-entities-node-detail__section">
              <h4 className="create-entities-node-detail__section-title">
                Attributes
              </h4>
              {selectedNodeAttributes.length === 0 ? (
                <div className="create-entities-node-detail__attributes-empty">
                  No attribute values are available for this node.
                </div>
              ) : (
                <div className="create-entities-node-detail__attributes-list">
                  {selectedNodeAttributes.map((attribute) => (
                    <div
                      key={attribute.id}
                      className="create-entities-node-detail__attribute"
                    >
                      <strong>{attribute.attribute}</strong>
                      <span>{attribute.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="create-entities-node-detail__section">
              <h4 className="create-entities-node-detail__section-title">
                Ontology Traceability
              </h4>
              <div className="create-entities-node-detail__traceability">
                <p className="create-entities-node-detail__traceability-label">
                  Reference example
                </p>
                <div className="create-entities-node-detail__traceability-group">
                  <h5>Business Question</h5>
                  {selectedNodeDetail.businessQuestions.length === 0 ? (
                    <p className="create-entities-node-detail__traceability-empty">
                      Not recorded yet
                    </p>
                  ) : (
                    selectedNodeDetail.businessQuestions.map((bq) => (
                      <div
                        key={bq.businessQuestionId}
                        className="create-entities-node-detail__bq-card"
                      >
                        <span className="create-entities-node-detail__bq-badge">
                          {bq.bqCode}
                        </span>
                        <p>{bq.questionText}</p>
                      </div>
                    ))
                  )}
                </div>
                <div className="create-entities-node-detail__traceability-group">
                  <h5>Competency Questions</h5>
                  {selectedNodeDetail.competencyQuestions.length === 0 ? (
                    <p className="create-entities-node-detail__traceability-empty">
                      Not recorded yet
                    </p>
                  ) : (
                    selectedNodeDetail.competencyQuestions.map((cq) => (
                      <div
                        key={cq.cqId}
                        className="create-entities-node-detail__cq-card"
                      >
                        <span className="create-entities-node-detail__cq-badge">
                          {cq.cqCode}
                        </span>
                        <p>{cq.questionText}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="create-entities-node-detail__attributes-empty">
            Unable to load node details.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="create-entities-related-entities-panel">
      <div className="create-entities-related-entities-panel__header">
        <span className="create-entities-related-entities-panel__title">
          Related Entities
        </span>
        <button
          type="button"
          className="create-entities-related-entities-panel__close"
          aria-label="Back to nodes"
          onClick={onClose}
        >
          <CloseOutlined fontSize="small" />
        </button>
      </div>
      {isInitialLoading ? (
        <div className="create-entities-node-detail__loading">
          <Spinner size={20} />
        </div>
      ) : hasData ? (
        <>
          <div className="create-entities-graph-directory__title-row">
            <span>Nodes ({1 + connectedNodes.length})</span>
            <span>{1 + filteredConnectedNodes.length} visible</span>
          </div>
          <div className="create-entities-graph-directory__search">
            <SearchOutlined fontSize="small" />
            <input
              type="text"
              placeholder="Filter nodes"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
            />
          </div>

          <p className="create-entities-node-detail__section-title">
            Focused node
          </p>
          {focusedNode && (
            <button
              type="button"
              className="create-entities-node-detail__summary create-entities-node-detail__summary--clickable"
              onClick={() => onSelectNode(focusedNode.id)}
            >
              <div className="create-entities-node-detail__identity">
                <span
                  className="create-entities-node-detail__swatch"
                  style={{
                    backgroundColor: getNodeColor(focusedNode.nodeType)
                  }}
                />
                <span className="create-entities-node-detail__name">
                  {focusedNode.label}
                </span>
                <span className="create-entities-node-detail__type-pill">
                  {focusedNode.nodeType}
                </span>
              </div>
              <p className="create-entities-node-detail__focus">
                {focusedNode.description || "No description available."}
              </p>
            </button>
          )}

          <p className="create-entities-node-detail__section-title">
            Connected nodes ({filteredConnectedNodes.length})
          </p>
          <div className="create-entities-related-entities-panel__connected-list">
            {filteredConnectedNodes.length === 0 ? (
              <div className="create-entities-node-detail__attributes-empty">
                No connected nodes found.
              </div>
            ) : (
              filteredConnectedNodes.map((entry) => (
                <button
                  type="button"
                  key={entry.id}
                  className="create-entities-related-entities-panel__connected-entry"
                  onClick={() => onSelectNode(entry.nodeId)}
                >
                  <div className="create-entities-node-detail__identity">
                    <span
                      className="create-entities-node-detail__swatch"
                      style={{ backgroundColor: getNodeColor(entry.nodeType) }}
                    />
                    <span className="create-entities-node-detail__name">
                      {entry.nodeName}
                    </span>
                    {entry.nodeType && (
                      <span className="create-entities-node-detail__type-pill">
                        {entry.nodeType}
                      </span>
                    )}
                  </div>
                  <p className="create-entities-related-entities-panel__connected-relation">
                    {entry.relationLabel}
                  </p>
                </button>
              ))
            )}
          </div>
        </>
      ) : (
        <div className="create-entities-node-detail__attributes-empty">
          Unable to load related entities.
        </div>
      )}
    </div>
  );
};

export default RelatedEntitiesPanel;
